import React from 'react';
import {
  ChevronRight as ChevronRightIcon,
  ConstructionOutlined as ConstructionOutlinedIcon,
  FavoriteBorder as FavoriteBorderIcon,
  PlaceOutlined as PlaceOutlinedIcon,
  RedeemOutlined as RedeemOutlinedIcon,
} from '@mui/icons-material';
import {
  projectProgramCategoryBadgeCompact,
  projectStatusLabel,
} from '../../utils/programLabels';
import { resolveAvanceeCatalogBannerLabel } from '../../utils/vitrineAvanceeTimeline';
import type { ProjectCardProject } from './ProjectCard';

interface FeaturedProgramCardProps {
  project: ProjectCardProject;
  onClick?: () => void;
  className?: string;
}

const capitalizeWord = (value: string): string =>
  value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();

const formatCityLabel = (value: string): string =>
  value
    .trim()
    .split(/[\s-]+/)
    .filter(Boolean)
    .map(capitalizeWord)
    .join('-');

const normalizeLocationText = (value: string): string =>
  value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[-/]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .toUpperCase();

const looksLikeStreetAddress = (value: string): boolean => {
  const trimmed = value.trim();
  return (
    /^\d/.test(trimmed) ||
    /\b(rue|avenue|place|allee|allée|chemin|route|impasse|boulevard|bd|voie|cours|quai|hameau)\b/i.test(
      trimmed,
    )
  );
};

const extractPostalCode = (value?: string): string | undefined => {
  const match = value?.match(/\b(\d{5})\b/);
  return match?.[1];
};

/** Ville + département uniquement — jamais l'adresse postale. */
const formatLocationLine = (project: ProjectCardProject): string => {
  const raw = project.location?.trim();
  const postalCode = extractPostalCode(raw);
  const dept = postalCode ? ` (${postalCode.slice(0, 2)})` : '';

  if (project.commune?.trim()) {
    return `${formatCityLabel(project.commune)}${dept}`;
  }

  if (raw) {
    const match = raw.match(/^(.+?)\s*[-–]\s*(\d{5})$/);
    if (match && !looksLikeStreetAddress(match[1])) {
      return `${formatCityLabel(match[1])} (${match[2].slice(0, 2)})`;
    }
  }

  const cityFromTitle = project.title.match(/^([A-Za-zÀ-ÿ'-]+)/)?.[1];
  if (cityFromTitle) {
    return `${formatCityLabel(cityFromTitle)}${dept}`;
  }

  return '';
};

const formatAddressLine = (project: ProjectCardProject): string => {
  if (project.streetAddress?.trim()) {
    return project.streetAddress.trim().toUpperCase();
  }

  return '';
};

const formatPrice = (price: number): string =>
  new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 0 }).format(price);

const FeaturedProgramCard: React.FC<FeaturedProgramCardProps> = ({
  project,
  onClick,
  className = '',
}) => {
  const displayName = project.title.split(' - ')[0].split(' — ')[0];
  const locationLine = formatLocationLine(project);
  const addressLine = formatAddressLine(project);
  const showAddressLine =
    Boolean(addressLine) &&
    normalizeLocationText(addressLine) !== normalizeLocationText(locationLine);
  const conditionLabel = project.conditionLabel ?? 'Neuf';
  const programTypeLabel =
    project.programTypeLabel ?? projectProgramCategoryBadgeCompact(project.type);

  const statusLabel =
    project.statusLabel?.trim() ||
    resolveAvanceeCatalogBannerLabel(project.avanceeTimeline, project.dateLivraison) ||
    projectStatusLabel(project.status);

  const promoTags = (project.tags || []).map((tag) => String(tag).trim()).filter(Boolean);
  const promoCandidates = [
    project.promoLabel?.trim(),
    project.secondaryBadgeLabel?.trim(),
    ...promoTags,
  ].filter(Boolean) as string[];
  const promoLabel =
    promoCandidates.find((label) =>
      /offert|notaire|promo|lancement|sp[ée]ciale|dispositif/i.test(label),
    ) ?? promoCandidates[0] ?? '';

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (onClick && (e.key === 'Enter' || e.key === ' ')) {
      e.preventDefault();
      onClick();
    }
  };

  const handleFavoriteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  return (
    <article
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onClick={onClick}
      onKeyDown={onClick ? handleKeyDown : undefined}
      className={`group/card featured-program-card relative flex h-full flex-col text-left motion-safe:transition-transform motion-safe:duration-300 motion-safe:group-hover/card:-translate-y-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-400/50 focus-visible:ring-offset-2 focus-visible:ring-offset-brand-graphite ${
        onClick ? 'cursor-pointer' : ''
      } ${className}`}
    >
      <div className="featured-program-card__surface flex h-full min-h-0 flex-col border border-neutral-700/80 bg-brand-charcoal motion-safe:transition-[border-color,box-shadow,background-color] motion-safe:duration-300 motion-safe:group-hover/card:border-neutral-600 motion-safe:group-hover/card:shadow-[0_16px_40px_-12px_rgba(0,0,0,0.45)]">
        <div className="relative shrink-0">
          <div className="relative aspect-[16/10] overflow-hidden bg-neutral-900">
            <img
              src={
                project.image ||
                'https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=1600&h=1000&fit=crop'
              }
              alt={project.title}
              className="h-full w-full object-cover motion-safe:transition-transform motion-safe:duration-700 motion-safe:group-hover/card:scale-[1.03]"
              loading="lazy"
              decoding="async"
            />

            <div className="absolute left-0 top-2.5 z-10 flex max-w-[72%] flex-col items-stretch gap-1 sm:top-3">
              <span className="featured-program-card__badge featured-program-card__badge--status">
                <ConstructionOutlinedIcon className="featured-program-card__badge-icon" aria-hidden />
                {statusLabel}
              </span>
              {promoLabel ? (
                <span className="featured-program-card__badge featured-program-card__badge--promo">
                  <RedeemOutlinedIcon className="featured-program-card__badge-icon" aria-hidden />
                  {promoLabel}
                </span>
              ) : null}
            </div>

            <button
              type="button"
              onClick={handleFavoriteClick}
              className="absolute right-3 top-3 z-10 flex h-9 w-9 items-center justify-center rounded-full border border-white/10 bg-black/45 text-white backdrop-blur-sm transition-colors hover:bg-black/60 focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-400/50 sm:h-10 sm:w-10"
              aria-label="Ajouter aux favoris"
            >
              <FavoriteBorderIcon className="h-[1.15rem] w-[1.15rem]" aria-hidden />
            </button>
          </div>
        </div>

        <div className="relative flex flex-1 flex-col px-4 pb-4 pt-8 sm:px-5 sm:pb-5 sm:pt-9">
          <div className="pointer-events-none absolute -top-5 right-4 z-20 sm:right-5">
            <span className="featured-program-card__discover pointer-events-auto">
              Découvrir
              <ChevronRightIcon className="featured-program-card__discover-icon h-4 w-4" aria-hidden />
            </span>
          </div>

          <h3 className="font-display line-clamp-2 text-[1.05rem] font-medium leading-snug tracking-[-0.02em] text-white sm:text-[1.125rem] lg:text-[1.2rem]">
            {displayName}
          </h3>

          {locationLine ? (
            <p className="mt-2 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.08em] text-teal-500 sm:text-[11px]">
              <PlaceOutlinedIcon className="h-3.5 w-3.5 shrink-0" aria-hidden />
              <span>{locationLine}</span>
            </p>
          ) : null}

          {showAddressLine ? (
            <p className="featured-program-card__address mt-1.5 line-clamp-1 text-[10px] font-medium uppercase tracking-[0.08em] sm:text-[11px]">
              {addressLine}
            </p>
          ) : null}

          <div className="mt-auto flex items-end justify-between gap-3 pt-4 sm:pt-5">
            <div className="flex min-w-0 flex-wrap items-center gap-2">
              <span className="featured-program-card__tag featured-program-card__tag--condition">
                {conditionLabel}
              </span>
              <span className="featured-program-card__tag featured-program-card__tag--type">
                {programTypeLabel}
              </span>
            </div>

            {project.priceMin != null && project.priceMin > 0 ? (
              <div className="shrink-0 text-right">
                <p className="text-[9px] font-medium uppercase tracking-[0.12em] text-white/45 sm:text-[10px]">
                  À partir de
                </p>
                <p className="mt-0.5 text-lg font-bold tabular-nums leading-none text-white sm:text-xl">
                  {formatPrice(project.priceMin)}&nbsp;€
                </p>
              </div>
            ) : (
              <p className="shrink-0 text-right text-sm font-semibold text-orange-500">Sur demande</p>
            )}
          </div>
        </div>
      </div>
    </article>
  );
};

export default FeaturedProgramCard;
